import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { CheckCircle2, AlertTriangle, XCircle, ChevronDown, ChevronUp, Loader } from 'lucide-react';
import Modal from './Modal';
import {
  masavApi,
  handleMasavApiError,
  MasavRowCategory,
  type MasavPreviewResult,
  type MasavRowResult,
  type MasavDuplicateResolution,
  type MasavCommitResult,
} from '../services/masavService';

interface MasavSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: File | null;
  month: string;
  preview: MasavPreviewResult | null;
  loading: boolean; // preview in progress
  error: string | null; // preview-level error
  onCommitted: () => void; // refresh the clients list after a successful commit
}

type CategoryKind = 'valid' | 'warn' | 'error' | 'muted';

interface CategoryMeta {
  label: string;
  kind: CategoryKind;
}

// Hebrew labels live here (UI layer). Keyed by the backend MasavRowCategory enum.
const CATEGORY_META: Record<MasavRowCategory, CategoryMeta> = {
  [MasavRowCategory.WillUpdate]: { label: 'יתעדכנו', kind: 'valid' },
  [MasavRowCategory.DuplicateDiffering]: { label: 'כפילויות הדורשות החלטה', kind: 'warn' },
  [MasavRowCategory.DuplicateExact]: { label: 'כפילויות מדויקות (ידולגו)', kind: 'warn' },
  [MasavRowCategory.CustomerNotIdentified]: { label: 'לא זוהה לקוח', kind: 'error' },
  [MasavRowCategory.NoActivePlan]: { label: 'אין תוכנית תשלום פעילה', kind: 'error' },
  [MasavRowCategory.NotUniquelyIdentified]: { label: 'זיהוי לא חד-משמעי', kind: 'error' },
  [MasavRowCategory.MissingRequiredFields]: { label: 'חסרים שדות חובה', kind: 'error' },
  [MasavRowCategory.InvalidValues]: { label: 'ערכים לא תקינים', kind: 'error' },
  [MasavRowCategory.NoPendingForMonth]: { label: 'אין תשלום ממתין לחודש שנבחר', kind: 'error' },
  [MasavRowCategory.EmptyRow]: { label: 'שורות ריקות', kind: 'muted' },
  [MasavRowCategory.CorruptText]: { label: 'קידוד/טקסט לא קריא', kind: 'error' },
};

// Display order of the category cards.
const CATEGORY_ORDER: MasavRowCategory[] = [
  MasavRowCategory.WillUpdate,
  MasavRowCategory.DuplicateDiffering,
  MasavRowCategory.DuplicateExact,
  MasavRowCategory.CustomerNotIdentified,
  MasavRowCategory.NoActivePlan,
  MasavRowCategory.NotUniquelyIdentified,
  MasavRowCategory.MissingRequiredFields,
  MasavRowCategory.InvalidValues,
  MasavRowCategory.NoPendingForMonth,
  MasavRowCategory.EmptyRow,
  MasavRowCategory.CorruptText,
];

const kindColor = (kind: CategoryKind): string => {
  switch (kind) {
    case 'valid': return 'var(--success-color)';
    case 'warn': return 'var(--warning-color)';
    case 'error': return 'var(--error-color)';
    default: return 'var(--text-secondary)';
  }
};

const formatAmount = (value?: number): string =>
  value == null ? '-' : `₪${value.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
`;

const CategoryCard = styled.button<{ $color: string; $active: boolean }>`
  text-align: center;
  background: ${props => (props.$active ? 'var(--hover-background)' : 'var(--surface-color)')};
  border: 1px solid ${props => (props.$active ? props.$color : 'var(--border-color)')};
  border-radius: var(--border-radius-md);
  padding: 12px 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover { background: var(--hover-background); }
`;

const CardNumber = styled.div<{ $color: string }>`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.$color};
`;

const CardLabel = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 4px;
`;

const RowsPanel = styled.div`
  max-height: 280px;
  overflow-y: auto;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  margin-bottom: 20px;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  background: var(--surface-color);
`;

const RowItem = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-color);
  font-size: 13px;

  &:last-child { border-bottom: none; }
`;

const RowMain = styled.div`
  font-weight: 500;
  color: var(--text-primary);
`;

const RowDetail = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 2px;
`;

const ChoiceRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
`;

const ChoiceLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  cursor: pointer;
  color: var(--text-primary);
`;

const InfoBar = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;
  color: var(--text-secondary);
  margin-bottom: 16px;
`;

const ErrorBox = styled.div`
  background: var(--error-light);
  color: var(--error-color);
  border: 1px solid var(--error-color);
  border-radius: var(--border-radius-sm);
  padding: 12px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const CenterState = styled.div`
  text-align: center;
  color: var(--text-secondary);
  padding: 40px 20px;
  font-size: 14px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  @media (max-width: 767px) { flex-direction: column; }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  ${props =>
    props.variant === 'primary'
      ? `background: var(--primary-color); color: #fff; border: 1px solid var(--primary-color);
         &:hover:not(:disabled){ background: transparent; color: var(--primary-color); }`
      : `background: var(--surface-color); color: var(--text-primary); border: 1px solid var(--border-color);
         &:hover:not(:disabled){ background: var(--hover-background); }`}
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ResultGrid = styled(SummaryGrid)`
  margin-top: 8px;
`;

const MasavSummaryModal: React.FC<MasavSummaryModalProps> = ({
  isOpen, onClose, file, month, preview, loading, error, onCommitted,
}) => {
  const [expanded, setExpanded] = useState<MasavRowCategory | null>(null);
  const [resolutions, setResolutions] = useState<Record<number, 'Update' | 'Skip'>>({});
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);
  const [commitResult, setCommitResult] = useState<MasavCommitResult | null>(null);

  const rowsByCategory = useMemo(() => {
    const map = new Map<MasavRowCategory, MasavRowResult[]>();
    preview?.rows.forEach(row => {
      const list = map.get(row.category) ?? [];
      list.push(row);
      map.set(row.category, list);
    });
    return map;
  }, [preview]);

  const validCount = rowsByCategory.get(MasavRowCategory.WillUpdate)?.length ?? 0;
  const differingRows = rowsByCategory.get(MasavRowCategory.DuplicateDiffering) ?? [];

  const handleResolution = (rowIndex: number, action: 'Update' | 'Skip') => {
    setResolutions(prev => ({ ...prev, [rowIndex]: action }));
  };

  const handleCommit = async () => {
    if (!file) return;
    try {
      setCommitting(true);
      setCommitError(null);
      const resolutionList: MasavDuplicateResolution[] = differingRows.map(r => ({
        rowIndex: r.rowIndex,
        action: resolutions[r.rowIndex] ?? 'Skip',
      }));
      const result = await masavApi.commit(file, month, resolutionList);
      setCommitResult(result);
      onCommitted();
    } catch (err) {
      setCommitError(`שגיאה בשמירת התשלומים: ${handleMasavApiError(err)}`);
    } finally {
      setCommitting(false);
    }
  };

  const handleClose = () => {
    setExpanded(null);
    setResolutions({});
    setCommitError(null);
    setCommitResult(null);
    onClose();
  };

  const renderRow = (row: MasavRowResult) => {
    const isDiffering = row.category === MasavRowCategory.DuplicateDiffering;
    return (
      <RowItem key={row.rowIndex}>
        <RowMain>
          שורה {row.rowIndex}: {row.matchedClientName || row.customerName || 'ללא שם'}
          {row.payerIdNumber ? ` · ת"ז משלם ${row.payerIdNumber}` : ''}
        </RowMain>
        <RowDetail>
          סכום בקובץ: {formatAmount(row.amount) }
          {row.expectedAmount != null ? ` · סכום צפוי: ${formatAmount(row.expectedAmount)}` : ''}
          {row.delta != null && row.delta !== 0
            ? row.delta > 0
              ? ` · זיכוי: ${formatAmount(row.delta)}`
              : ` · חוב: ${formatAmount(-row.delta)}`
            : ''}
          {row.existingAmount != null ? ` · סכום קיים: ${formatAmount(row.existingAmount)}` : ''}
          {row.reason ? ` · ${row.reason}` : ''}
        </RowDetail>
        {isDiffering && (
          <ChoiceRow>
            <ChoiceLabel>
              <input
                type="radio"
                name={`res-${row.rowIndex}`}
                checked={(resolutions[row.rowIndex] ?? 'Skip') === 'Update'}
                onChange={() => handleResolution(row.rowIndex, 'Update')}
              />
              עדכן
            </ChoiceLabel>
            <ChoiceLabel>
              <input
                type="radio"
                name={`res-${row.rowIndex}`}
                checked={(resolutions[row.rowIndex] ?? 'Skip') === 'Skip'}
                onChange={() => handleResolution(row.rowIndex, 'Skip')}
              />
              דלג
            </ChoiceLabel>
          </ChoiceRow>
        )}
      </RowItem>
    );
  };

  const renderPreview = () => {
    if (loading) {
      return <CenterState><Loader size={28} className="spin" /><div>מנתח את הקובץ...</div></CenterState>;
    }
    if (error) {
      return <ErrorBox>{error}</ErrorBox>;
    }
    if (!preview) {
      return <CenterState>אין נתונים להצגה</CenterState>;
    }
    if (preview.fileError) {
      return (
        <>
          <ErrorBox>{preview.fileError}</ErrorBox>
          <ButtonGroup><Button onClick={handleClose}>סגור</Button></ButtonGroup>
        </>
      );
    }

    const expandedRows = expanded != null ? rowsByCategory.get(expanded) ?? [] : [];

    return (
      <>
        <InfoBar>
          <span>קובץ: {preview.fileName}</span>
          <span>חודש דיווח: {preview.selectedMonth}</span>
          <span>סה"כ שורות: {preview.totalRows}</span>
          <span>קידוד: {preview.detectedEncoding}</span>
        </InfoBar>

        <SummaryGrid>
          {CATEGORY_ORDER.map(cat => {
            const count = rowsByCategory.get(cat)?.length ?? 0;
            if (count === 0) return null;
            const meta = CATEGORY_META[cat];
            const color = kindColor(meta.kind);
            return (
              <CategoryCard
                key={cat}
                $color={color}
                $active={expanded === cat}
                onClick={() => setExpanded(expanded === cat ? null : cat)}
              >
                <CardNumber $color={color}>{count}</CardNumber>
                <CardLabel>{meta.label}</CardLabel>
              </CategoryCard>
            );
          })}
        </SummaryGrid>

        {expanded != null && expandedRows.length > 0 && (
          <RowsPanel>
            <PanelHeader>
              {expanded === MasavRowCategory.WillUpdate ? <CheckCircle2 size={16} />
                : CATEGORY_META[expanded].kind === 'error' ? <XCircle size={16} />
                : <AlertTriangle size={16} />}
              {CATEGORY_META[expanded].label} ({expandedRows.length})
              {expanded != null ? <ChevronUp size={16} style={{ marginInlineStart: 'auto' }} /> : <ChevronDown size={16} />}
            </PanelHeader>
            {expandedRows.map(renderRow)}
          </RowsPanel>
        )}

        {commitError && <ErrorBox>{commitError}</ErrorBox>}

        <ButtonGroup>
          <Button onClick={handleClose} disabled={committing}>בטל</Button>
          <Button
            variant="primary"
            onClick={handleCommit}
            disabled={committing || validCount + differingRows.length === 0}
          >
            {committing ? 'מעלה...' : 'העלה בכל זאת'}
          </Button>
        </ButtonGroup>
      </>
    );
  };

  const renderResult = (result: MasavCommitResult) => (
    <>
      <ResultGrid>
        <CategoryCard as="div" $color={kindColor('valid')} $active={false}>
          <CardNumber $color={kindColor('valid')}>{result.savedCount}</CardNumber>
          <CardLabel>נשמרו</CardLabel>
        </CategoryCard>
        <CategoryCard as="div" $color="var(--primary-color)" $active={false}>
          <CardNumber $color="var(--primary-color)">{result.updatedCount}</CardNumber>
          <CardLabel>עודכנו</CardLabel>
        </CategoryCard>
        <CategoryCard as="div" $color={kindColor('warn')} $active={false}>
          <CardNumber $color={kindColor('warn')}>{result.skippedCount}</CardNumber>
          <CardLabel>דולגו</CardLabel>
        </CategoryCard>
        {result.failedCount > 0 && (
          <CategoryCard as="div" $color={kindColor('error')} $active={false}>
            <CardNumber $color={kindColor('error')}>{result.failedCount}</CardNumber>
            <CardLabel>נכשלו</CardLabel>
          </CategoryCard>
        )}
      </ResultGrid>

      {result.errors.length > 0 && (
        <RowsPanel style={{ marginTop: 16 }}>
          <PanelHeader><XCircle size={16} />שגיאות</PanelHeader>
          {result.errors.map((e, i) => <RowItem key={i}>{e}</RowItem>)}
        </RowsPanel>
      )}

      <ButtonGroup style={{ marginTop: 20 }}>
        <Button variant="primary" onClick={handleClose}>סגור</Button>
      </ButtonGroup>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title='סיכום ייבוא תשלומי מס"ב'
      width="760px"
      maxWidth="95vw"
    >
      {commitResult ? renderResult(commitResult) : renderPreview()}
    </Modal>
  );
};

export default MasavSummaryModal;
